import React, { useState, useEffect } from "react";
import { FaRegEdit } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import "./styles/RequestForm.css";
import Done from "../assets/images/login-tick 1.png";
import { advertisingAgentAPI } from "../services/advertisingAgentService";

export default function RequestForm({ agent }) {
  const navigate = useNavigate();

  // Get application ID from agent
  const applicationId = agent?.id || agent?.application_id;

  // Determine initial agent type
  const initialAgentType = agent?.agent_type ||
    (agent?.type?.toLowerCase()?.includes('senior') ? 'senior' : 'mini');

  const [formData, setFormData] = useState({
    name: agent?.first_name || agent?.name || agent?.customer_details?.first_name || "",
    email: agent?.email || agent?.customer_details?.email || "",
    type: initialAgentType,
    requestDate: agent?.applied_at ? new Date(agent.applied_at).toLocaleDateString('en-CA').replace(/-/g, '/') : "",
    status: agent?.status || "pending",
    notes: agent?.rejection_reason || agent?.notes || "",
    commissionRate: agent?.agent_details?.commission_rate || agent?.commission_rate ||
      (initialAgentType === 'senior' ? 15.00 : 10.00),
    isAlreadyAgent: agent?.is_already_agent || false,
    agentId: agent?.agent_details?.agent_id || agent?.agent_id || null,
    photoUrl: agent?.photo_url || "",
  });

  const [isUpdated, setIsUpdated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Update form when agent prop changes
  useEffect(() => {
    if (agent) {
      const initialAgentType = agent?.agent_type ||
        (agent?.type?.toLowerCase()?.includes('senior') ? 'senior' : 'mini');

      setFormData({
        name: agent?.first_name || agent?.name || agent?.customer_details?.first_name || "",
        email: agent?.email || agent?.customer_details?.email || "",
        type: initialAgentType,
        requestDate: agent?.reviewed_at ? new Date(agent.reviewed_at).toLocaleDateString('en-CA').replace(/-/g, '/') : "",
        status: agent?.status || "pending",
        notes: agent?.rejection_reason || agent?.notes || "",
        commissionRate: agent?.agent_details?.commission_rate || agent?.commission_rate ||
          (initialAgentType === 'senior' ? 15.00 : 10.00),
        isAlreadyAgent: agent?.is_already_agent || false,
        agentId: agent?.agent_details?.agent_id || agent?.agent_id || null,
        photoUrl: agent?.photo_url || "",
      });
    }
  }, [agent]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Auto-update commission rate when type changes
    if (name === "type") {
      const newCommissionRate = value === "senior" ? 15.00 : (value === "customer" ? 0 : 10.00);
      setFormData({
        ...formData,
        type: value,
        commissionRate: newCommissionRate,
      });
      return;
    }

    // Keep the original auto-date behavior for Approved status
    if (name === "status" && value === "approved") {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      const currentDate = `${yyyy}/${mm}/${dd}`;

      setFormData({
        ...formData,
        status: value,
        requestDate: currentDate,
        notes: "",
      });
      return;
    }

    // Clear notes when changing from rejected
    if (name === "status" && formData.status === "rejected" && value !== "rejected") {
      setFormData({
        ...formData,
        status: value,
        notes: "",
      });
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!applicationId) {
      setError("No application ID found");
      return;
    }

    setLoading(true);
    setError("");

    try {
      let result;

      if (formData.type === 'customer') {
        if (!formData.agentId) {
          throw new Error("No agent ID found for demoting. Use 'Reject' status for new applications.");
        }


        result = await advertisingAgentAPI.demoteToCustomer(
          formData.agentId,
          formData.notes
        );
        setFormData(prev => ({ ...prev, status: 'rejected' }));
      } else {
        if (formData.status === "approved") {
          // For approval - with ability to change agent type
          result = await advertisingAgentAPI.reviewAgentApplication(
            applicationId,
            "approved",
            "", // No rejection reason
            formData.commissionRate,
            formData.type // Pass the agent type
          );

        } else if (formData.status === "rejected") {
          // For rejection
          result = await advertisingAgentAPI.reviewAgentApplication(
            applicationId,
            "rejected",
            formData.notes, // Rejection reason
            formData.commissionRate,
            formData.type // Pass the agent type
          );

        } else if (formData.status === "pending") {
          // For editing application (changing type while keeping pending)
          result = await advertisingAgentAPI.reviewAgentApplication(
            applicationId,
            "pending",
            formData.notes || "",
            formData.commissionRate,
            formData.type // Pass the agent type
          );
        }
      }

      setIsUpdated(true);

    } catch (err) {
      console.error("Error processing application:", err);
      setError(err.response?.data?.message || err.message || "Failed to process application");
    } finally {
      setLoading(false);
    }
  };

  // Direct conversion to agent (without application)
  const handleDirectConvert = async () => {
    if (!agent?.user_id && !agent?.customer_details?.user_id) {
      setError("No user ID found for direct conversion");
      return;
    }

    const userId = agent?.user_id || agent?.customer_details?.user_id;

    setLoading(true);
    setError("");

    try {
      await advertisingAgentAPI.createDirectAdvertisingAgent(
        userId,
        formData.type,
        formData.commissionRate,
        true, // isActive
        formData.notes || "Direct conversion by admin"
      );

      setIsUpdated(true);

    } catch (err) {
      console.error("Error in direct conversion:", err);
      setError(err.response?.data?.message || "Failed to convert customer to agent");
    } finally {
      setLoading(false);
    }
  };

  // Update existing agent type
  const handleUpdateAgentType = async () => {
    if (!formData.agentId) {
      setError("No agent ID found for updating");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await advertisingAgentAPI.updateAgentType(
        formData.agentId,
        formData.type,
        formData.commissionRate,
        formData.notes || `Type changed to ${formData.type}`
      );

      setIsUpdated(true);

    } catch (err) {
      console.error("Error updating agent type:", err);
      setError(err.response?.data?.message || "Failed to update agent type");
    } finally {
      setLoading(false);
    }
  };



  const handleCancel = () => {
    navigate(-1);
  };

  const handleOkay = () => {
    navigate("/manage-advertising-agent");
  };

  const isEditing = agent?.status === "pending";
  const isAlreadyAgent = formData.isAlreadyAgent;
  const isApproved = formData.status === "approved";

  return (
    <div className="approval-form-container">
      <IoClose className="form-close" onClick={handleCancel} />

      {!isUpdated ? (
        <>
          <h2>{isEditing ? "Edit Application" : "Approval Request"}</h2>

          {/* Agent Photo */}
          {formData.photoUrl && (
            <div className="agent-photo-container">
              <img
                src={formData.photoUrl}
                alt="Agent"
                className="agent-photo"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          <form onSubmit={handleSubmit} className="approval-form">
            {/* Read-only customer info */}
            <label>Name</label>
            <input value={formData.name} readOnly maxLength={255} />

            <label>Email</label>
            <input value={formData.email} readOnly maxLength={255} />

            {/* Agent Type - Admin can change this */}
            <label>Type</label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              disabled={loading}
              style={{ color: "darkblue" }}
            >
              <option value="customer">Customer</option>
              <option value="mini">Mini Advertising Agent</option>
              <option value="senior">Senior Advertising Agent</option>
            </select>

            {/* Commission Rate - editable based on type */}
            {/* <label>Commission Rate (%)</label>
            <input
              type="number"
              name="commissionRate"
              value={formData.commissionRate}
              onChange={handleChange}
              min="0"
              max="100"
              step="0.01"
              disabled={loading}
              style={{color: "darkblue"}}
            /> */}

            <label>{isApproved ? "Approve Date" : "Request Date"}</label>
            <input
              type="text"
              name="requestDate"
              value={formData.requestDate}
              readOnly
              onChange={handleChange}
              placeholder="yyyy/mm/dd"
              maxLength={255} />

            {/* Status selection */}
            <label>Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              disabled={loading}
              style={{ color: "darkblue" }}
            >
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>

            {/* Notes/Rejection Reason */}
            {(formData.status === "rejected" || formData.notes) && (
              <>
                <label>{formData.status === "rejected" ? "Reason" : "Notes"}</label>
                <div className="textarea-wrapper">
                  <textarea
                    name="notes"
                    placeholder={formData.status === "rejected" ? "Enter reason here" : "Enter notes here"}
                    value={formData.notes}
                    onChange={handleChange}
                    disabled={loading}
                    maxLength={65535} />
                  <FaRegEdit className="textarea-icon" />
                </div>
              </>
            )}

            {/* Error message */}
            {error && (
              <div style={{ color: 'red', textAlign: 'center', margin: '10px 0' }}>
                {error}
              </div>
            )}

            {/* Action buttons */}
            <div className="form-actions">
              {/* Main action button */}
              <button
                type="submit"
                className="btn-update"
                disabled={loading}
              >
                {loading ? "Processing..." :
                  formData.status === "approved" ? "Update" :
                    formData.status === "rejected" ? "Update" :
                      "Update"}
              </button>

              {/* Direct conversion button (for non-agents) - positioned like original cancel */}
              {/* {!isAlreadyAgent && (
                <button 
                  type="button"
                  className="btn-cancel"
                  onClick={handleDirectConvert}
                  disabled={loading}
                  style={{backgroundColor: "#28a745", color: "white", border: "none"}}
                >
                  Direct Convert
                </button>
              )} */}

              {/* Update agent type button (for existing agents) */}
              {/* {isAlreadyAgent && formData.agentId && (
                <button 
                  type="button"
                  className="btn-cancel"
                  onClick={handleUpdateAgentType}
                  disabled={loading}
                  style={{backgroundColor: "#ffc107", color: "#000", border: "none"}}
                >
                  Update Type
                </button>
              )} */}


              {/* Cancel button */}
              <button
                type="button"
                className="btn-cancel"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </form>
        </>
      ) : (
        <div className="approval-card">
          <img src={Done} alt="" className="done" />
          <h2>{formData.status === 'approved' ? 'Approved' : formData.status === 'rejected' ? 'Rejected' : 'Updated'}</h2>
          <h2 style={{ marginTop: "-15px", marginBottom: "30px" }}>
            {formData.type === 'customer' ? 'Customer' : (formData.type === 'senior' ? 'Senior' : 'Mini')} Advertising Agent Request
          </h2>
          <button className="btn-okay" onClick={handleOkay}>
            Okay
          </button>
        </div>
      )}
    </div>
  );
}
